import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import * as Fonts from "../../../../components/Fonts";
import constants from "../../../../constants";
import PlusIcon from "../../../../icons/Plus";
import TrashIcon from "../../../../icons/Trash";
import { ListCard, ListCardAction, VisibilityIcon } from "../ListCard";
import { AddItemBox, CardItems, Container } from "./styles";

export interface Item {
  id: string;
  title: string;
  thumbnailType?: "IMAGE" | "EMOJI" | "NONE";
  image?: string;
  emoji?: string;
  active?: boolean;
  /** Shown on a colored square instead of the image/emoji thumbnail (e.g. a form field's type). */
  icon?: ReactNode;
  /** Second line under the title. */
  description?: string;
}

type Props = {
  title: string;
  addButtonTitle: string;
  items: Item[];
  onAddClick: () => void;
  onRemoveClick: (id: string) => void;
  onEditClick: (id: string) => void;
  /** Shows a duplicate action on each row (lists that can copy an item; absent, no such action). */
  onDuplicateClick?: (id: string) => void;
  /** Called with the updated list after a reorder or a show/hide. */
  onSortUpdate: (items: Item[]) => void;
  /** Shows the show/hide action regardless of `constants.features.itemVisibility` (lists the page
   * renderer already honors). */
  withVisibility?: boolean;
  /** Greys out the add button (e.g. the list reached its limit). */
  addDisabled?: boolean;
};

// Two sheets, one over the other: a copy of the item
const DuplicateIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <rect x="8" y="8" width="13" height="13" rx="2" />
    <path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" />
  </svg>
);

const thumbnailOf = ({ thumbnailType, image, emoji }: Item) => {
  if (thumbnailType === "IMAGE" && image) {
    return <img src={image} alt="" loading="lazy" />;
  }
  if (thumbnailType === "EMOJI" && emoji) {
    return emoji;
  }
  return undefined;
};

const ItemsTable = ({
  title,
  addButtonTitle,
  items,
  onAddClick,
  onRemoveClick,
  onEditClick,
  onSortUpdate,
  onDuplicateClick,
  withVisibility = constants.features.itemVisibility,
  addDisabled = false,
}: Props) => {
  const { t } = useTranslation();

  // Flips `active` keeping every other field of the item (the pages pass their full item objects)
  const toggleVisibility = (id: string) =>
    onSortUpdate(items.map(item => (item.id === id ? { ...item, active: item.active === false } : item)));

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    const itemsClone = Array.from(items);
    const [reorderedItem] = itemsClone.splice(result.source.index, 1);
    itemsClone.splice(result.destination.index, 0, reorderedItem);

    onSortUpdate(itemsClone);
  };

  return (
    <Container>
      <Fonts.Label>{title}</Fonts.Label>
      <CardItems>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="items" direction="vertical">
            {provided => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {items.map((item, index) => (
                  <Draggable key={item.id} index={index} draggableId={item.id}>
                    {provided => (
                      <div
                        className="item"
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}>
                        <ListCard
                          title={item.title}
                          description={item.description}
                          leading={item.icon || thumbnailOf(item)}
                          leadingVariant={item.icon ? "icon" : "media"}
                          dimmed={withVisibility && item.active === false}
                          onClick={() => onEditClick(item.id)}
                          actions={
                            <>
                              {withVisibility && (
                                <ListCardAction
                                  type="button"
                                  title={t(`components.itemsTable.${item.active === false ? "show" : "hide"}`)}
                                  aria-label={t(`components.itemsTable.${item.active === false ? "show" : "hide"}`)}
                                  onClick={e => {
                                    toggleVisibility(item.id);
                                    e.stopPropagation();
                                  }}>
                                  <VisibilityIcon visible={item.active !== false} />
                                </ListCardAction>
                              )}
                              {onDuplicateClick && (
                                <ListCardAction
                                  type="button"
                                  title={t("components.itemsTable.duplicate")}
                                  aria-label={t("components.itemsTable.duplicate")}
                                  onClick={e => {
                                    onDuplicateClick(item.id);
                                    e.stopPropagation();
                                  }}>
                                  <DuplicateIcon />
                                </ListCardAction>
                              )}
                              <ListCardAction
                                type="button"
                                $danger
                                title={t("components.itemsTable.remove")}
                                aria-label={t("components.itemsTable.remove")}
                                onClick={e => {
                                  onRemoveClick(item.id);
                                  e.stopPropagation();
                                }}>
                                <TrashIcon size={16} color="currentColor" />
                              </ListCardAction>
                            </>
                          }
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder as ReactNode}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </CardItems>
      <AddItemBox type="button" disabled={addDisabled} onClick={() => onAddClick()}>
        <PlusIcon width={12} height={12} color="currentColor" />
        {addButtonTitle}
      </AddItemBox>
    </Container>
  );
};

export default ItemsTable;
